import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { dynamicConfigApi } from '../../services/dynamicConfigApi';
import { DynamicConfigEntry } from '../../types/dynamicConfig';
import { useAuth } from '../../hooks/useAuth';
import ConfigKeyEditor, { defaultDraft, formatWhen } from './ConfigKeyEditor';
import ConfigHistoryDialog from './ConfigHistoryDialog';

/**
 * Admin → Configuration → Runtime Config tab (moved verbatim from DynamicConfigPage in Spec 061;
 * only the class names changed to the styled admin-* equivalents).
 *
 * <p>Every key a service reads, with the description that says what changing it does. A key with no
 * override shows "Using the deployed value" rather than a number: the backend does not report the
 * deployed value, because a second copy of it on this screen could quietly go stale.
 *
 * <p>Saving takes effect within about a minute — each service polls platform on that interval.
 * Reverting removes the override and hands the key back to the value its service was deployed with.
 *
 * <p>#1113 (decision 3B) — only keys whose `adminScreen` is `dynamic-config` are listed, even for a
 * super admin: the service fee, Tabby and Zofeur (#1251) keys are edited on Finance → Configuration. The
 * one-way delivery switch (`finance`) is hidden everywhere in the portal. A pointer line says where the Finance keys went.
 */

/** A key this screen edits: `adminScreen` is `dynamic-config` (absent on a pre-#1113 platform). */
export function isRuntimeConfigKey(entry: DynamicConfigEntry): boolean {
  return (entry.adminScreen ?? 'dynamic-config') === 'dynamic-config';
}

const PROPAGATION_NOTE =
  'Changes reach the running services within about a minute. Nothing restarts, and anything '
  + 'already in flight keeps the value it started with.';

export default function RuntimeConfigTab() {
  const auth = useAuth();
  const [entries, setEntries] = useState<DynamicConfigEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  /** Per-key editor state, keyed by config key. Absent means "not being edited". */
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [rowError, setRowError] = useState<Record<string, string>>({});
  const [savedKey, setSavedKey] = useState<string | null>(null);

  const [historyKey, setHistoryKey] = useState<string | null>(null);

  const load = useCallback(() => {
    if (!auth.token) return;
    setLoading(true);
    setError(null);
    dynamicConfigApi
      .list(auth.token)
      .then((all) => setEntries((all ?? []).filter(isRuntimeConfigKey)))
      .catch((err: Error) => setError(err.message))
      .finally(() => setLoading(false));
  }, [auth.token]);

  useEffect(() => {
    load();
  }, [load]);

  /** Grouped by owning service, preserving the order the backend sent. */
  const groups = useMemo(() => {
    const byService = new Map<string, DynamicConfigEntry[]>();
    entries.forEach((entry) => {
      const bucket = byService.get(entry.ownerService) ?? [];
      bucket.push(entry);
      byService.set(entry.ownerService, bucket);
    });
    return [...byService.entries()];
  }, [entries]);

  const draftFor = (entry: DynamicConfigEntry) =>
    drafts[entry.key] ?? entry.value ?? defaultDraft(entry);

  const setDraft = (key: string, value: string) =>
    setDrafts((prev) => ({ ...prev, [key]: value }));

  const clearRowError = (key: string) =>
    setRowError((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });

  async function save(entry: DynamicConfigEntry) {
    if (!auth.token) return;
    setBusyKey(entry.key);
    clearRowError(entry.key);
    try {
      const updated = await dynamicConfigApi.set(entry.key, draftFor(entry), auth.token);
      setEntries((prev) => prev.map((e) => (e.key === entry.key ? updated : e)));
      setDrafts((prev) => {
        const next = { ...prev };
        delete next[entry.key];
        return next;
      });
      setSavedKey(entry.key);
      window.setTimeout(() => setSavedKey((k) => (k === entry.key ? null : k)), 4000);
    } catch (err) {
      setRowError((prev) => ({ ...prev, [entry.key]: (err as Error).message }));
    } finally {
      setBusyKey(null);
    }
  }

  async function revert(entry: DynamicConfigEntry) {
    if (!auth.token) return;
    const ok = window.confirm(
      `Revert ${entry.key} to the value the ${entry.ownerService} service was deployed with?`,
    );
    if (!ok) return;

    setBusyKey(entry.key);
    clearRowError(entry.key);
    try {
      await dynamicConfigApi.clear(entry.key, auth.token);
      setEntries((prev) =>
        prev.map((e) =>
          e.key === entry.key
            ? { ...e, overridden: false, value: null, updatedAt: null, updatedBy: null, updatedByName: null }
            : e,
        ),
      );
      setDrafts((prev) => {
        const next = { ...prev };
        delete next[entry.key];
        return next;
      });
    } catch (err) {
      setRowError((prev) => ({ ...prev, [entry.key]: (err as Error).message }));
    } finally {
      setBusyKey(null);
    }
  }

  function openHistory(key: string) {
    if (!auth.token) return;
    setHistoryKey(key);
  }

  const financeConfigLink = auth.hasScreen('finance-config')
    ? <Link to="/finance?tab=configuration">Finance → Configuration</Link>
    : <strong>Finance → Configuration</strong>;

  if (loading) return <div className="admin-loading">Loading…</div>;
  if (error) return <div className="admin-error">{error}</div>;

  return (
    <div>
      <div
        className="admin-page-header"
        style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16 }}
      >
        <div>
          <h2 style={{ fontSize: '1.125rem', fontWeight: 600, margin: '0 0 0.25rem' }}>Runtime Config</h2>
          <p className="admin-page-subtitle">{PROPAGATION_NOTE}</p>
        </div>
        <button className="admin-btn admin-btn-outline" onClick={load}>Refresh</button>
      </div>

      <div className="admin-info-banner info" role="note" data-testid="runtime-config-pointer">
        Service fee, Tabby and Zofeur settings are in {financeConfigLink}.
      </div>

      {groups.map(([service, rows]) => (
        <section key={service} style={{ marginBottom: 32 }}>
          <h2 style={{ textTransform: 'capitalize' }}>{service}</h2>
          <div className="admin-table-wrapper">
            <table className="admin-table">
              <thead>
                <tr>
                  <th style={{ width: '34%' }}>Setting</th>
                  <th style={{ width: '22%' }}>Value</th>
                  <th style={{ width: '22%' }}>Last changed</th>
                  <th style={{ width: '22%' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((entry) => {
                  const busy = busyKey === entry.key;
                  const dirty = drafts[entry.key] !== undefined && drafts[entry.key] !== entry.value;
                  return (
                    <tr key={entry.key}>
                      <td>
                        <code>{entry.key}</code>
                        <p className="admin-page-subtitle" style={{ margin: '4px 0 0' }}>
                          {entry.description}
                        </p>
                      </td>
                      <td>
                        <ConfigKeyEditor
                          entry={entry}
                          ariaLabel={`${entry.key} value`}
                          value={draftFor(entry)}
                          disabled={busy}
                          onChange={(v) => setDraft(entry.key, v)}
                        />
                        {!entry.overridden && (
                          <p className="admin-page-subtitle" style={{ margin: '4px 0 0' }}>
                            Using the deployed value
                          </p>
                        )}
                        {rowError[entry.key] && (
                          <p className="admin-error" style={{ margin: '6px 0 0' }}>
                            {rowError[entry.key]}
                          </p>
                        )}
                        {savedKey === entry.key && (
                          <p className="admin-page-subtitle" style={{ margin: '4px 0 0' }}>
                            Saved — live within a minute
                          </p>
                        )}
                      </td>
                      <td>
                        {entry.overridden ? (
                          <>
                            <div>{entry.updatedByName ?? entry.updatedBy ?? 'unknown'}</div>
                            <div className="admin-page-subtitle">{formatWhen(entry.updatedAt)}</div>
                          </>
                        ) : (
                          <span className="admin-page-subtitle">Never changed here</span>
                        )}
                      </td>
                      <td>
                        <button
                          className="admin-btn admin-btn-primary"
                          onClick={() => save(entry)}
                          disabled={busy || !dirty}
                        >
                          {busy ? 'Saving…' : 'Save'}
                        </button>
                        {entry.overridden && (
                          <button
                            className="admin-btn admin-btn-outline"
                            style={{ marginLeft: 8 }}
                            onClick={() => revert(entry)}
                            disabled={busy}
                          >
                            Revert
                          </button>
                        )}
                        <button
                          className="admin-btn admin-btn-outline"
                          style={{ marginLeft: 8 }}
                          onClick={() => openHistory(entry.key)}
                        >
                          History
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
      ))}

      {historyKey && (
        <ConfigHistoryDialog
          title="History"
          keys={[historyKey]}
          token={auth.token as string}
          onClose={() => setHistoryKey(null)}
        />
      )}
    </div>
  );
}
